/**
 * Question Card (Checkup assessment flow)
 */

import { useTranslation, useLocale } from '@tonnie/locale';
import {
  Button,
  Card,
  CardContent,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Slider,
} from '@tonnie/ui';
import { CheckCircle2, FileText, Send, Upload } from '@tonnie/assets';
import { useId, useRef, useState } from 'react';
import type { IMessage } from './types';

interface IQuestionCardProps {
  question: IMessage;
  isLoading: boolean;
  onSelectOption: (option: string | number) => void;
  onSendMessage: (message: string) => void;
}

export function QuestionCard({
  question,
  isLoading,
  onSelectOption,
  onSendMessage,
}: IQuestionCardProps) {
  const { t } = useTranslation();
  // Several question cards can be on screen at once, so every id is scoped to this card.
  const cardId = useId();
  const questionTextId = `${cardId}-question`;
  const { formatFileSize, formatPercent } = useLocale();
  const [selectedOption, setSelectedOption] = useState('');
  const [ratingValue, setRatingValue] = useState(3);
  const [input, setInput] = useState('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSelectOption = (option: string) => {
    setSelectedOption(option);
    onSelectOption(option);
  };

  const handleRatingSubmit = () => {
    onSelectOption(ratingValue);
    setRatingValue(3);
  };

  const handleTextSubmit = () => {
    if (input.trim()) {
      onSendMessage(input);
      setInput('');
    }
  };

  return (
    <div className="flex justify-start" data-testid="guide-question">
      <Card className="w-full max-w-xs lg:max-w-md border-primary/20 shadow-sm">
        <CardContent className="p-4 space-y-4">
          <p id={questionTextId} className="font-medium text-sm text-foreground">
            {question.content}
          </p>

          {question.questionType === 'mcq' && question.options && (
            <RadioGroup
              value={selectedOption}
              onValueChange={handleSelectOption}
              aria-labelledby={questionTextId}
            >
              <div className="space-y-2">
                {question.options.map((option, idx) => (
                  <div
                    key={idx}
                    className="flex items-center space-x-2 group cursor-pointer"
                    data-testid={`guide-question-option-${idx}`}
                  >
                    <RadioGroupItem value={option} id={`${cardId}-option-${idx}`} />
                    <Label
                      htmlFor={`${cardId}-option-${idx}`}
                      className="text-sm cursor-pointer flex-1 group-hover:text-primary-accessible transition-colors"
                    >
                      {option}
                    </Label>
                  </div>
                ))}
              </div>
            </RadioGroup>
          )}

          {question.questionType === 'rating' && (
            <div className="space-y-3">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{t('checkup.chat.notAtAll')}</span>
                <span>{t('checkup.chat.veryMuch')}</span>
              </div>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((num) => (
                  <Button
                    key={num}
                    variant={ratingValue === num ? 'default' : 'outline'}
                    size="sm"
                    className="w-8 h-8 p-0 rounded-full"
                    onClick={() => setRatingValue(num)}
                  >
                    {num}
                  </Button>
                ))}
              </div>
              <Button
                onClick={handleRatingSubmit}
                className="w-full h-8 text-xs"
                disabled={isLoading}
                data-testid="guide-question-submit"
              >
                {t('common.submit')}
              </Button>
            </div>
          )}

          {question.questionType === 'slider' && (
            <div className="space-y-3">
              <Slider
                aria-labelledby={questionTextId}
                value={[ratingValue]}
                onValueChange={(val) => setRatingValue(val[0])}
                min={0}
                max={100}
                step={5}
                className="w-full"
              />
              <div className="text-center">
                <p className="text-lg font-bold text-primary-accessible">
                  {formatPercent(ratingValue)}
                </p>
              </div>
              <Button
                onClick={handleRatingSubmit}
                className="w-full h-8 text-xs"
                disabled={isLoading}
                data-testid="guide-question-submit"
              >
                {t('common.submit')}
              </Button>
            </div>
          )}

          {question.questionType === 'text' && (
            <div className="space-y-2">
              <Input
                placeholder={t('checkup.chat.typeYourAnswer')}
                aria-labelledby={questionTextId}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleTextSubmit()}
                className="text-sm"
                data-testid="guide-question-input"
              />
              <Button
                onClick={handleTextSubmit}
                className="w-full h-8 text-xs"
                disabled={isLoading || !input.trim()}
                data-testid="guide-question-submit"
              >
                <Send className="w-3 h-3 mr-1" />
                {t('common.submit')}
              </Button>
            </div>
          )}

          {(question.questionType === 'upload' || question.taskConfig?.type === 'upload') && (
            <div className="space-y-3">
              <input
                ref={fileInputRef}
                type="file"
                accept={question.taskConfig?.accept || '.pdf,.doc,.docx'}
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) setUploadedFile(file);
                }}
              />
              {uploadedFile ? (
                <div className="p-3 bg-success/10 rounded-lg border border-success/30 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-success-accessible" />
                    <div>
                      <p className="text-sm font-medium">{uploadedFile.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatFileSize(uploadedFile.size)}
                      </p>
                    </div>
                  </div>
                  <CheckCircle2 className="w-5 h-5 text-success-accessible" />
                </div>
              ) : (
                <Button
                  variant="outline"
                  className="w-full h-20 border-dashed"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div className="flex flex-col items-center gap-2">
                    <Upload className="w-6 h-6" />
                    <span className="text-xs">{t('checkup.chat.clickToUpload')}</span>
                  </div>
                </Button>
              )}
              <Button
                onClick={() => uploadedFile && onSelectOption(uploadedFile.name)}
                className="w-full h-8 text-xs"
                disabled={isLoading || !uploadedFile}
                data-testid="guide-question-submit"
              >
                {t('common.submit')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
